/**
 * Os marcos do kickoff de uma implantação e o caminho da K-O dela.
 *
 * `modo` diz quem gera a K-O: `"jira"` é o caminho de sempre, que vale enquanto a escrita no
 * GeoTask estiver desligada (e para quem já teve a K-O gerada pelo Jira); `"geotask"` é a K-O
 * nascida no GeoTask depois do aceite. Sem o campo, vale o Jira.
 */

import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { pedirJson } from "./api";

export const esquemaDosMarcos = z.object({
  tarefa_gerada_em: z.string().nullable(),
  concluido_em: z.string().nullable(),
  acompanhamento_liberado: z.boolean(),
  jira_pendencia: z.string().nullable(),
  modo: z.enum(["jira", "geotask"]).optional().default("jira"),
  geotask_key: z.string().nullable().optional(),
  geotask_pendencia: z.string().nullable().optional(),
  aviso_segmento: z.string().nullable().optional(),
});

export type MarcosDoKickoff = z.infer<typeof esquemaDosMarcos>;
export type ModoDoKickoff = MarcosDoKickoff["modo"];

export function chaveDosMarcos(implantacaoId: string) {
  return ["console", implantacaoId, "marcos-kickoff"] as const;
}

export function caminhoDosMarcos(implantacaoId: string): string {
  return `/implantacoes/${implantacaoId}/kickoff`;
}

/** Os marcos; no caminho do Jira, repete a leitura enquanto a tarefa aguarda o envio. */
export function useMarcosDoKickoff(implantacaoId: string, { habilitado = true } = {}) {
  return useQuery({
    queryKey: chaveDosMarcos(implantacaoId),
    enabled: habilitado,
    queryFn: () => pedirJson(`${caminhoDosMarcos(implantacaoId)}/marcos`, esquemaDosMarcos),
    refetchInterval: (consulta) => {
      const dados = consulta.state.data;
      return dados?.modo === "jira" && dados.jira_pendencia && !dados.tarefa_gerada_em ? 3000 : false;
    },
  });
}

/** O caminho da K-O; `undefined` enquanto os marcos carregam (quem desenha decide o neutro). */
export function useModoDoKickoff(
  implantacaoId: string,
  opcoes: { habilitado?: boolean } = {},
): ModoDoKickoff | undefined {
  return useMarcosDoKickoff(implantacaoId, opcoes).data?.modo;
}
